import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearWaistMeasurements,
    deleteWaistMeasurement,
    loadWaistMeasurements
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import { WAIST_MEASUREMENTS_ERROR } from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import CreateWaistMeasurement from './CreateWaistMeasurement';
import WaistMeasurementRow from './WaistMeasurementRow';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'measurementDate', numeric: false, label: 'Date' },
    { id: 'measurement', numeric: true, label: 'Measurement (cm)' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'measurementDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const WaistMeasurements = ({
    participant,
    isDisabled = true,
    isDeleteDisabled = true,
    isAuditable,
    onSearchAuditItems
}) => {
    const dispatch = useDispatch();
    const waistMeasurementsRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');

    // STORE STATE
    const waistMeasurements = useSelector(
        (state) => state.entities.participantService.waistMeasurements
    );
    const waistMeasurementsMetaData = useSelector(
        (state) => state.entities.participantService.waistMeasurementsMetaData
    );

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        participant?.id && dispatch(loadWaistMeasurements(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (waistMeasurementsMetaData?.loaded) {
            if (!waistMeasurements?.length) {
                setRows([]);
                setLoadingContentMessage(WAIST_MEASUREMENTS_ERROR);
            } else setRows([...waistMeasurements]);
        }
    }, [waistMeasurements, waistMeasurementsMetaData]);

    // FNs
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <WaistMeasurementRow
                key={el.id}
                row={el}
                isDisabled={isDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const cleanUp = () => {
        dispatch(clearWaistMeasurements());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setFormType('edit');
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteWaistMeasurement(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(waistMeasurementsRef);
    };

    // RENDER
    return (
        <div
            className={local.wellBeingSection}
            data-testid="waist_measurements"
            ref={waistMeasurementsRef}>
            <h2 className={app.sectionHeading}>Waist Measurements</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addWaistMeasurementButton"
                        type="button"
                        content="Add New Waist Measurement"
                        disabled={isDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <CreateWaistMeasurement
                        participant={participant}
                        onClose={onClose}
                        isDisabled={isDisabled}
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

WaistMeasurements.propTypes = {
    participant: PropTypes.object.isRequired,
    isDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default WaistMeasurements;
